import { useEffect, useState } from "react"
import { useParams } from "react-router-dom"
import { toast } from "react-toastify"
import styled from "styled-components"

import SupportTicketService from "~services/SupportTicketService"
import UserService from "~services/UserService"
import { useAuthContext } from "~context/authContext"
import BackLink from "~components/BackLink"
import { Card } from "~components/Card"
import IsAuthorized from "~rbac/isAuthorized"
import { RBAC_RULES } from "~rbac/constants/rbac-rules"
import PATHS, { getFullPath } from "~constants/paths"
import {
  TicketShape,
  TicketMessageShape,
  TicketStatus,
  TicketCategory,
  TICKET_STATUS_LABELS,
  TICKET_CATEGORY_LABELS,
  ALL_TICKET_STATUSES,
} from "~shapes/support-ticket"
import { UserShape } from "~shapes/user"
import UserHeaderWithActions from "~components/users/UserHeaderWithActions"
import UserDetailTabs from "~components/users/UserDetailTabs"
import colors from "~constants/colors"

const SupportTicket = () => {
  const { id } = useParams<{ id: string }>()
  const { loggedUser } = useAuthContext()

  const backTo =
    sessionStorage.getItem("supportTicketsBack") ??
    getFullPath(PATHS.supportTickets)

  const [ticket, setTicket] = useState<TicketShape | null>(null)
  const [user, setUser] = useState<UserShape | null>(null)
  const [messages, setMessages] = useState<TicketMessageShape[]>([])
  const [isLoadingTicket, setIsLoadingTicket] = useState(true)
  const [isLoadingUser, setIsLoadingUser] = useState(false)
  const [isLoadingMessages, setIsLoadingMessages] = useState(true)
  const [newMessage, setNewMessage] = useState("")
  const [isSendingMessage, setIsSendingMessage] = useState(false)
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false)
  const [markWaitingForUser, setMarkWaitingForUser] = useState(true)

  useEffect(() => {
    if (!id) return
    loadTicket()
    loadMessages()
    SupportTicketService.markTicketAsRead(id).catch(() => {
      // non-blocking: failure to record the read checkpoint shouldn't affect the view
    })
  }, [id])

  const loadTicket = async () => {
    setIsLoadingTicket(true)
    try {
      const data = await SupportTicketService.getTicket(id!)
      setTicket(data)
      if (data.user_id) loadUser(data.user_id)
    } catch {
      toast.error("Failed to load ticket.")
    } finally {
      setIsLoadingTicket(false)
    }
  }

  const loadUser = async (userId: string) => {
    setIsLoadingUser(true)
    try {
      const data = await UserService.getUserById(userId, false)
      setUser(data)
    } catch {
      // user not found — silently ignore, panel will stay hidden
    } finally {
      setIsLoadingUser(false)
    }
  }

  const loadMessages = async () => {
    setIsLoadingMessages(true)
    try {
      const data = await SupportTicketService.getMessages(id!, {
        page_size: 100,
      })
      setMessages(data.items)
    } catch {
      toast.error("Failed to load messages.")
    } finally {
      setIsLoadingMessages(false)
    }
  }

  const handleStatusChange = async (
    e: React.ChangeEvent<HTMLSelectElement>,
  ) => {
    const newStatus = e.target.value as TicketStatus
    setIsUpdatingStatus(true)
    try {
      const updated = await SupportTicketService.updateTicketStatus(
        id!,
        newStatus,
      )
      setTicket(updated)
      toast.success("Status updated.")
    } catch {
      toast.error("Failed to update status.")
    } finally {
      setIsUpdatingStatus(false)
    }
  }

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!newMessage.trim()) return
    setIsSendingMessage(true)
    try {
      const msg = await SupportTicketService.addMessage(id!, {
        author_type: "AGENT",
        author_id: loggedUser?.id ?? "",
        message: newMessage.trim(),
      })
      setMessages(prev => [...prev, msg])
      setNewMessage("")

      if (markWaitingForUser && ticket?.status !== "WAITING_FOR_USER") {
        const updated = await SupportTicketService.updateTicketStatus(
          id!,
          "WAITING_FOR_USER",
        )
        setTicket(updated)
      }

      // Mark the ticket as read for this agent so the reply we just sent
      // doesn't cause the ticket to appear unread in the list.
      SupportTicketService.markTicketAsRead(id!).catch(() => {})
    } catch {
      toast.error("Failed to send message.")
    } finally {
      setIsSendingMessage(false)
    }
  }

  if (isLoadingTicket) {
    return (
      <>
        <BackLink text="Back to Support Tickets" path={backTo} />
        <Card>
          <p>Loading ticket...</p>
        </Card>
      </>
    )
  }

  if (!ticket) {
    return (
      <>
        <BackLink text="Back to Support Tickets" path={backTo} />
        <Card>
          <p>Ticket not found.</p>
        </Card>
      </>
    )
  }

  const ticketTitle =
    TICKET_CATEGORY_LABELS[ticket.title as TicketCategory] ?? ticket.title
  const ticketCategory = ticket.category
    ? (TICKET_CATEGORY_LABELS[ticket.category as TicketCategory] ??
      ticket.category)
    : null

  const showUserPanel = !!ticket.user_id && (isLoadingUser || !!user)

  return (
    <>
      <BackLink text="Back to Support Tickets" path={backTo} />

      <SplitLayout>
        <ConversationColumn>
          <Card>
            <h3 style={{ marginTop: 0, marginBottom: 20 }}>Conversation</h3>

            <MessageThread>
              {isLoadingMessages ? (
                <p>Loading messages...</p>
              ) : messages.length === 0 ? (
                <p style={{ color: colors.gray3 }}>No messages yet.</p>
              ) : (
                messages.map(msg => (
                  <MessageBubble key={msg.id} $authorType={msg.author_type}>
                    <MessageMeta>
                      <span style={{ fontWeight: "bold" }}>
                        {msg.author_type === "USER"
                          ? `User (${msg.author_id})`
                          : msg.author_type === "AGENT"
                            ? `Agent (${msg.author_id})`
                            : "System"}
                      </span>
                      <span style={{ fontSize: 12, color: colors.gray3 }}>
                        {new Date(msg.created_at).toLocaleString()}
                      </span>
                    </MessageMeta>
                    <MessageBody>{msg.message}</MessageBody>
                  </MessageBubble>
                ))
              )}
            </MessageThread>

            <IsAuthorized action={RBAC_RULES.CREATE_TICKET_MESSAGE}>
              <ReplyForm onSubmit={handleSendMessage}>
                <textarea
                  value={newMessage}
                  onChange={e => setNewMessage(e.target.value)}
                  placeholder="Write a reply..."
                  rows={7}
                  style={{
                    width: "100%",
                    padding: 12,
                    borderRadius: 6,
                    border: "1px solid #ddd",
                    fontSize: 14,
                    resize: "vertical",
                    boxSizing: "border-box",
                  }}
                />
                <ReplyActions>
                  <CheckboxLabel>
                    <input
                      type="checkbox"
                      checked={markWaitingForUser}
                      onChange={e => setMarkWaitingForUser(e.target.checked)}
                    />
                    Mark as Waiting for User
                  </CheckboxLabel>
                  <button
                    type="submit"
                    disabled={isSendingMessage || !newMessage.trim()}
                    style={{
                      height: 40,
                      padding: "0 20px",
                      borderRadius: 6,
                      border: "none",
                      backgroundColor: colors.blue1,
                      color: "white",
                      fontWeight: "bold",
                      cursor: "pointer",
                      opacity: isSendingMessage || !newMessage.trim() ? 0.5 : 1,
                    }}
                  >
                    {isSendingMessage ? "Sending..." : "Send Reply"}
                  </button>
                </ReplyActions>
              </ReplyForm>
            </IsAuthorized>
          </Card>
        </ConversationColumn>

        <DetailsColumn>
          {/* Ticket header */}
          <Card padding="18">
            <TicketHeader>
              <TitleBlock>
                <SectionLabel>Ticket details</SectionLabel>
                <TicketTitle>{ticketTitle}</TicketTitle>
              </TitleBlock>

              <IsAuthorized action={RBAC_RULES.UPDATE_TICKET_STATUS}>
                <StatusRow>
                  <StatusLabel>Status</StatusLabel>
                  <CompactSelect
                    value={ticket.status}
                    onChange={handleStatusChange}
                    disabled={isUpdatingStatus}
                  >
                    {ALL_TICKET_STATUSES.map(s => (
                      <option key={s} value={s}>
                        {TICKET_STATUS_LABELS[s]}
                      </option>
                    ))}
                  </CompactSelect>
                </StatusRow>
              </IsAuthorized>
            </TicketHeader>

            <MetaGrid>
              <MetaItem>
                <MetaKey>Priority</MetaKey>
                <MetaValue>{ticket.priority}</MetaValue>
              </MetaItem>
              {ticketCategory && (
                <MetaItem>
                  <MetaKey>Category</MetaKey>
                  <MetaValue>{ticketCategory}</MetaValue>
                </MetaItem>
              )}
              <MetaItem>
                <MetaKey>Created</MetaKey>
                <MetaValue>
                  {new Date(ticket.created_at).toLocaleString()}
                </MetaValue>
              </MetaItem>
              <MetaItem>
                <MetaKey>Updated</MetaKey>
                <MetaValue>
                  {new Date(ticket.updated_at).toLocaleString()}
                </MetaValue>
              </MetaItem>
            </MetaGrid>

            {ticket.description && (
              <Description>{ticket.description}</Description>
            )}
          </Card>

          {/* User header (reused from /users/:email) */}
          {showUserPanel && (
            <UserHeaderWithActions
              user={user!}
              isLoading={isLoadingUser}
              updateUser={setUser}
              afterDeletePath={backTo}
            />
          )}

          {/* User tabs */}
          {showUserPanel && (
            <UserDetailTabs
              user={user!}
              isLoading={isLoadingUser}
              defaultActiveTabId="tickets"
              highlightedTicketId={ticket.id}
            />
          )}
        </DetailsColumn>
      </SplitLayout>
    </>
  )
}

export default SupportTicket

// Ticket styles
const SectionLabel = styled.div`
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.6px;
  text-transform: uppercase;
  color: ${colors.gray6};
  margin-bottom: 4px;
`

const TicketHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
`

const TitleBlock = styled.div`
  min-width: 0;
  flex: 1;
`

const TicketTitle = styled.h2`
  margin: 0;
  font-size: 18px;
  line-height: 1.25;
  color: ${colors.gray3};
`

const MetaGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px 18px;
  margin-top: 12px;
`

const MetaItem = styled.div`
  display: flex;
  align-items: baseline;
  gap: 6px;
  font-size: 12px;
`

const MetaKey = styled.span`
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  color: ${colors.gray7};
`

const MetaValue = styled.span`
  color: ${colors.gray3};
  font-weight: 500;
`

const StatusRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
`

const StatusLabel = styled.span`
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  color: ${colors.gray7};
`

const CompactSelect = styled.select`
  height: 30px;
  padding: 0 8px;
  border-radius: 6px;
  border: 1px solid ${colors.gray2};
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  background: ${colors.white};

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
`

const Description = styled.p`
  margin: 12px 0 0;
  padding-top: 12px;
  border-top: 1px solid ${colors.gray2};
  font-size: 13px;
  color: ${colors.gray3};
  white-space: pre-wrap;
`

// Split layout
const SplitLayout = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 460px) minmax(0, 1fr);
  gap: 16px;
  align-items: start;

  @media (max-width: 1100px) {
    grid-template-columns: minmax(0, 1fr);
  }
`

const ConversationColumn = styled.div`
  min-width: 0;
  position: sticky;
  top: 16px;
  max-height: calc(100vh - 32px);

  @media (max-width: 1100px) {
    position: static;
    max-height: none;
  }
`

const DetailsColumn = styled.div`
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 16px;
`

// Message styles
const MessageThread = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 24px;
  max-height: calc(100vh - 320px);
  min-height: 320px;
  overflow-y: auto;

  @media (max-width: 1100px) {
    max-height: 500px;
  }
`

const MessageBubble = styled.div<{ $authorType: string }>`
  background-color: ${({ $authorType }) =>
    $authorType === "USER"
      ? "#f0f4ff"
      : $authorType === "AGENT"
        ? "#f6fff6"
        : "#fafafa"};
  border: 1px solid ${colors.gray2};
  border-radius: 8px;
  padding: 12px 16px;
`

const MessageMeta = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 6px;
  font-size: 13px;
`

const MessageBody = styled.p`
  margin: 0;
  font-size: 14px;
  white-space: pre-wrap;
`

const ReplyForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 8px;
`

const ReplyActions = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 16px;
`

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: ${colors.gray3};
  cursor: pointer;
  user-select: none;
`
